// The keys of the Ctrl mode (docs/tooltips-plan.md §6, D736–D752, D759).
//
// Two kinds of key. **The static map** (D747): Ctrl with T, A, F, S, H, E, O
// or N does one thing from any focus, at once — the next tab and the next chat
// (with Shift the one before, with a number that one), search, settings, the
// call, emoji, attach, and what can be made (the plus's menu, D759). Tabs and
// chats take a frame that each press moves and Ctrl let go presses (D748).
// **Everything else is dynamic**: hold Ctrl alone for 400 ms and every control
// on screen shows the key that presses it — a control without words, its tip
// with the key after the words; a control with words, a row or a field, the
// key alone at its edge (D741). The zone the focus is in has keys of one
// letter, every other zone its letter and then the control's own (D738,
// D739); a chat row or a person with the focus shows its menu's actions beside
// it (D740). Those keys work only while they are drawn (D749), one press per
// hold (D736). The feed takes no part (D751): its messages have keys of their
// own (`feedkeys.js`, D752), and menus and panels their arrows
// (`menukeys.js`, D760–D763).
//
// The markup says what the script cannot guess:
//
//     data-hotkey="search"         what a chord of the map presses (D747):
//                                  search, settings, hangup, answer, call,
//                                  emoji, attach, new
//     data-hotkey="chats"          a list a chord walks — tabs, chats — and
//     data-hotkey-item             its items, the current one aria-selected or
//                                  aria-current (D748)
//     data-keytip-zone="composer"  a zone: list, dock, header, composer, call,
//                                  sheet, menu
//     data-keytip="M"              this control's own letter in its zone
//     data-keytip-rows             a list whose rows take the digits first
//     data-keytip-object           a thing whose menu stands beside it while it
//                                  has the focus…
//     data-keytip-actions          …drawn hidden here: the menu's own rows
//     data-keytip-none             nothing in here takes part
//     data-keytip-relay            a sheet over this window whose card is drawn
//                                  in another one (the desktop's floating
//                                  layer, D744): the keys are passed on to it
//     data-keytips="off"           on the window's root: no legend and no
//                                  dynamic key (D743) — the map still works
//     data-keytips-taken="M D"     on the window's root: letters that are not
//                                  this script's — the reader's own chords
//                                  with Ctrl (D739), what a browser tab keeps
//                                  (D747)
//
//     window.__echoKeytips.up()          whether the legend is drawn
//     window.__echoKeytips.plan()        what it would draw now, as
//                                        [{el, code, kind, focus, fixed}] —
//                                        for the tests and the stand
//     window.__echoKeytips.map()         the static map, as [{name, keys}]
//     window.__echoKeytips.chord(name)   a mark's chord, «Ctrl+H», for its tip
//     window.__echoKeytips.remote(msg)   the relay's far end: «hold», «up»,
//                                        «key:R», «down» (D744)
//     window.__echoHotkey(name)          the client's, where it has one: takes
//                                        a chord's press for itself and
//                                        answers true (Settings, D747)
//
// The pressing is the page's own: `click()` for a control, `focus()` for a
// field. Nothing crosses to Rust or React to find a control or to press it,
// for the reason `tips.js` gives — the document is the one complete list of
// what is on screen (D731) — but what the page does not draw: the settings
// screen. Shared by both clients for the reason `tips.js` is.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoKeytips) return;

  // D736: how long Ctrl is held alone before the keys come up.
  var HOLD = 400;
  var MOUNT = ".echo-tips";
  var BOX = "echo-keytips";
  var ZONE = "[data-keytip-zone]";
  var ROWS = "[data-keytip-rows]";
  var OBJECT = "[data-keytip-object]";
  var ACTIONS = "[data-keytip-actions]";
  var NONE = "[data-keytip-none]";
  var RELAY = "[data-keytip-relay]";
  var ITEM = "[data-hotkey-item]";
  // D747: the static map, in the order Settings lists it. A `list` walks its
  // items with a frame (D748); a `free` chord needs nothing on screen to act.
  var MAP = [
    { name: "tabs", key: "T", list: true },
    { name: "chats", key: "A", list: true },
    { name: "search", key: "F" },
    { name: "settings", key: "S", free: true },
    { name: "call", key: "H" },
    { name: "emoji", key: "E" },
    { name: "attach", key: "O" },
    { name: "new", key: "N" },
  ];
  // Ctrl+H's marks, in the order it looks for them (D747): the call that is,
  // the call that rings, the call this chat may have.
  var CALL = ["hangup", "answer", "call"];
  // D749: the letters the map leaves, in the order an element that declares
  // none is given one — D first, which no zone wants for its letter, and R
  // last, so Ctrl+R stays the browser's wherever nothing chose it.
  var ORDER = "DQPGJLMR";
  var DIGITS = "1234567890";
  // D749: each zone's own letter, for its keys of two while the focus zone
  // leaves the letter free; the order the zones choose in; and where a zone
  // whose letter is taken looks next — the least wanted first.
  var ZONE_LETTER = { composer: "M", list: "L", header: "G", dock: "J", call: "P" };
  var ZONE_ORDER = ["composer", "list", "header", "dock", "call"];
  var WINDOW_LETTER = "Q";
  var FALLBACK = "QPGJLMDR";
  // D727's distances, which the tips held up keep.
  var GAP = 6;
  var EDGE = 8;
  var CONTROLS =
    "button, a[href], input:not([type=hidden]), select, textarea, " +
    "[contenteditable=''], [contenteditable=true], " +
    "[role=button], [role=switch], [role=checkbox], [role=radio], [role=tab], " +
    "[role=slider], [role=menuitem], [role=option]";
  // Pressed by taking the focus rather than by a click (D736).
  var FIELDS =
    "input:not([type]), input[type=text], input[type=search], input[type=email], " +
    "input[type=password], input[type=number], input[type=url], input[type=tel], " +
    "input[type=range], textarea, select, [contenteditable=''], [contenteditable=true], [role=slider]";
  // Content takes no part (the owner's answer 3): a message and everything in
  // it, and what phase 1 left as content (`tips.js`'s list, docs/tooltips-plan.md §5.4).
  var CONTENT =
    ".message-bubble, .emoji-panel__glyph, .quick-reactions__cell, .reaction-chip, " +
    ".sticker-panel__cell, .composer__stickers-cell, .message-sticker, .media-album__cell, " +
    ".media-album__play, .attachment-chip__open, .stream-view__frame, .media-album__slide";

  // idle → held → up → narrowed, and spent after a press until Ctrl is let go
  // (docs/tooltips-plan.md §6.1).
  var state = "idle";
  var timer = 0;
  var drawn = null; // the plan on screen while up or narrowed
  var typed = ""; // the keys of a longer code typed so far
  var relaying = false; // this hold's keys belong to another window (D744)
  // A list's chord while Ctrl is held (D748): which list, its items, the
  // framed one, the digits typed, whether the numbers are up.
  var walk = null;

  function letterSet(text) {
    var out = {};
    for (var i = 0; i < text.length; i++) out[text.charAt(i)] = true;
    return out;
  }

  function takenLetters() {
    var root = document.querySelector("[data-keytips-taken]");
    return letterSet(root === null ? "" : (root.getAttribute("data-keytips-taken") || "").toUpperCase());
  }

  // ── What takes part (D737) ──

  function disabled(el) {
    return el.disabled === true || el.getAttribute("aria-disabled") === "true";
  }

  function takesPart(el) {
    if (el.closest(NONE) !== null || el.closest(ACTIONS) !== null || el.closest(MOUNT) !== null) return false;
    if (el.closest(CONTENT) !== null) return false;
    if (el.getAttribute("role") === "img" || disabled(el)) return false;
    return true;
  }

  // Inside the window, of some size, and what the engine would hit at its
  // middle is it or inside it: that alone drops what a scrim, a sheet or a
  // menu's dismiss sheet covers, and what is scrolled out of its list.
  function uncovered(el) {
    var r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;
    var x = r.left + r.width / 2;
    var y = r.top + r.height / 2;
    if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return null;
    var hit = document.elementFromPoint(x, y);
    if (hit === null || (hit !== el && !el.contains(hit))) return null;
    return r;
  }

  // ── The static map (D747) ──

  function entryOf(symbol) {
    for (var i = 0; i < MAP.length; i++) if (MAP[i].key === symbol) return MAP[i];
    return null;
  }

  // The chord a mark answers to — Ctrl+H's three are the call's — unless its
  // letter is somebody else's here.
  function chord(mark) {
    var name = CALL.indexOf(mark) >= 0 ? "call" : mark;
    for (var i = 0; i < MAP.length; i++) {
      if (MAP[i].name === name) return takenLetters()[MAP[i].key] ? "" : "Ctrl+" + MAP[i].key;
    }
    return "";
  }

  // A field with words in it: Ctrl+A selects them there, as everywhere (the
  // owner's answer of 2026-10-20; the rule D461 gives Escape).
  function typedField(t) {
    if (!(t instanceof Element)) return false;
    if (t.isContentEditable) return true;
    if (t.tagName === "TEXTAREA") return t.value !== "";
    if (t.tagName !== "INPUT") return false;
    var type = (t.getAttribute("type") || "text").toLowerCase();
    return ["text", "search", "email", "url", "tel", "password", "number"].indexOf(type) >= 0 && t.value !== "";
  }

  function marked(mark) {
    return document.querySelectorAll('[data-hotkey="' + mark + '"]');
  }

  // What a mark presses: the one nothing covers.
  function topmost(mark) {
    var all = marked(mark);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest(NONE) !== null || el.closest(ACTIONS) !== null || disabled(el)) continue;
      if (uncovered(el) !== null) return el;
    }
    return null;
  }

  // …or wherever it is, under a sheet too: ending a call is never out of reach.
  function anywhere(mark) {
    var all = marked(mark);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest(ACTIONS) !== null || el.closest("[hidden]") !== null || disabled(el)) continue;
      if (el.getClientRects().length > 0) return el;
    }
    return null;
  }

  function callMark() {
    return anywhere("hangup") || anywhere("answer") || topmost("call");
  }

  // The client takes the press for itself: what the page does not draw.
  function client(name) {
    return typeof window.__echoHotkey === "function" && window.__echoHotkey(name) === true;
  }

  // A chord of the map that is not a list's.
  function act(entry) {
    var el = entry.name === "call" ? callMark() : topmost(entry.name);
    if (el === null && !entry.free) return;
    if (client(entry.name)) return;
    if (el !== null) press({ el: el, kind: el.matches(FIELDS) ? "field" : "key" });
  }

  // ── Tabs and chats: a frame, and Ctrl let go (D748) ──

  function itemsOf(list) {
    var out = [];
    var all = list.querySelectorAll(ITEM);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest("[hidden]") !== null || disabled(el) || el.getClientRects().length === 0) continue;
      out.push(el);
    }
    return out;
  }

  // The list a chord walks: the one with an item nothing covers.
  function listOf(name) {
    var all = marked(name);
    for (var i = 0; i < all.length; i++) {
      if (all[i].closest(NONE) !== null) continue;
      var items = itemsOf(all[i]);
      for (var j = 0; j < items.length; j++) {
        if (uncovered(items[j]) !== null) return items;
      }
    }
    return null;
  }

  function currentOf(items) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute("aria-selected") === "true") return i;
      var current = items[i].getAttribute("aria-current");
      if (current !== null && current !== "false") return i;
    }
    return -1;
  }

  function around(i, n) {
    return ((i % n) + n) % n;
  }

  function begin(entry, d) {
    var shown = state === "up" || state === "narrowed";
    // The chord is what this hold does now: the legend gives way, whether or
    // not it finds its list.
    spend();
    var items = listOf(entry.name);
    if (items === null) return;
    var n = items.length;
    var current = currentOf(items);
    walk = {
      entry: entry,
      items: items,
      at: current < 0 ? (d > 0 ? 0 : n - 1) : around(current + d, n),
      digits: "",
      shown: shown && !off(),
      timer: 0,
      frame: 0,
      stamp: "",
    };
    // The numbers come up with the legend's threshold, so a quick chord is
    // no flash of them; the frame is at once.
    if (!walk.shown && !off()) {
      walk.timer = window.setTimeout(function () {
        if (walk === null) return;
        walk.timer = 0;
        walk.shown = true;
        show();
      }, HOLD);
    }
    reveal();
  }

  function move(d) {
    walk.at = around(walk.at + d, walk.items.length);
    walk.digits = "";
    reveal();
  }

  // A digit of the item's number: 1 then 2 is 12; one that makes a number no
  // item has starts it again.
  function digit(g) {
    var n = walk.items.length;
    var sofar = walk.digits + g;
    var k = parseInt(sofar, 10);
    if (!(k >= 1 && k <= n)) {
      sofar = g;
      k = parseInt(g, 10);
    }
    if (!(k >= 1 && k <= n)) return;
    walk.digits = sofar;
    walk.at = k - 1;
    reveal();
  }

  // The least move of the nearest scroller that shows el — by hand, as the
  // feed's walk does, so no clipping ancestor of the window is ever scrolled.
  function into(el) {
    var r = el.getBoundingClientRect();
    for (var p = el.parentElement; p !== null; p = p.parentElement) {
      var style = getComputedStyle(p);
      var down = /(auto|scroll)/.test(style.overflowY) && p.scrollHeight > p.clientHeight;
      var across = /(auto|scroll)/.test(style.overflowX) && p.scrollWidth > p.clientWidth;
      if (!down && !across) continue;
      var box = p.getBoundingClientRect();
      if (down) {
        if (r.top < box.top) p.scrollTop -= box.top - r.top;
        else if (r.bottom > box.bottom) p.scrollTop += Math.min(r.bottom - box.bottom, r.top - box.top);
      }
      if (across) {
        if (r.left < box.left) p.scrollLeft -= box.left - r.left;
        else if (r.right > box.right) p.scrollLeft += Math.min(r.right - box.right, r.left - box.left);
      }
      return;
    }
  }

  function reveal() {
    into(walk.items[walk.at]);
    walk.stamp = "";
    show();
    follow();
  }

  // The frame on its item, and the numbers once they are up — the items whose
  // number starts with the digits typed so far.
  function show() {
    var b = box();
    if (b === null || walk === null) return;
    var target = walk.items[walk.at];
    var r = target.getBoundingClientRect();
    var stamp = [r.left, r.top, r.width, r.height, walk.at, walk.digits, walk.shown].join(",");
    if (stamp === walk.stamp) return;
    walk.stamp = stamp;
    b.textContent = "";
    var frame = document.createElement("div");
    frame.className = "echo-keytips-frame";
    frame.style.left = Math.round(r.left) + "px";
    frame.style.top = Math.round(r.top) + "px";
    frame.style.width = Math.round(r.width) + "px";
    frame.style.height = Math.round(r.height) + "px";
    frame.style.borderRadius = getComputedStyle(target).borderRadius;
    b.appendChild(frame);
    if (!walk.shown) return;
    var sofar = walk.entry.key + walk.digits;
    for (var i = 0; i < walk.items.length; i++) {
      var number = String(i + 1);
      if (number.indexOf(walk.digits) !== 0) continue;
      var at = uncovered(walk.items[i]);
      if (at === null) continue;
      var key = document.createElement("div");
      key.className = "echo-keytip";
      caps(key, walk.entry.key + number, sofar);
      b.appendChild(key);
      pin(key, at);
    }
  }

  // Kept on its item while anything moves under it — a smooth scroll, a row
  // arriving — for as long as Ctrl is held.
  function follow() {
    if (walk === null || walk.frame || typeof window.requestAnimationFrame !== "function") return;
    var tick = function () {
      if (walk === null) return;
      walk.frame = window.requestAnimationFrame(tick);
      show();
    };
    walk.frame = window.requestAnimationFrame(tick);
  }

  function drop() {
    if (walk === null) return;
    if (walk.timer) window.clearTimeout(walk.timer);
    if (walk.frame && typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(walk.frame);
    walk = null;
    clear();
  }

  // Ctrl let go: the framed item is pressed. Nothing on the way was.
  function commit() {
    var w = walk;
    drop();
    if (w !== null && w.items[w.at].isConnected) w.items[w.at].click();
  }

  // A chord of the map. Whether it was one — then it is the window's.
  function hotkey(e, symbol) {
    var entry = entryOf(symbol);
    if (entry === null || takenLetters()[symbol]) return false;
    if (e.shiftKey && !entry.list) return false;
    if (symbol === "A" && !e.shiftKey && typedField(e.target)) return false;
    var d = e.shiftKey ? -1 : 1;
    if (entry.list) {
      if (walk !== null && walk.entry === entry) move(d);
      else {
        drop();
        begin(entry, d);
      }
      return true;
    }
    drop();
    spend();
    if (!e.repeat) act(entry);
    return true;
  }

  // ── Collecting the dynamic keys (D737) ──

  function collect(skip) {
    var out = [];
    var nodes = document.querySelectorAll(CONTROLS);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (skip.has(el) || !takesPart(el)) continue;
      if (uncovered(el) === null) continue;
      out.push({ el: el, kind: kindOf(el), row: el.closest(ROWS) !== null, own: "" });
    }
    return out;
  }

  // A field takes the focus; a control whose tip has words shows them with its
  // key; everything else, its words on screen already, shows the key alone.
  function kindOf(el) {
    if (el.matches(FIELDS)) return "field";
    var words = window.__echoTips ? window.__echoTips.words(el) : "";
    return words ? "icon" : "key";
  }

  // The map's keys on screen now (D747): each mark's, and a list's items with
  // their numbers — drawn like the rest, pressed by their chords.
  function fixed() {
    var out = [];
    var taken = takenLetters();
    for (var i = 0; i < MAP.length; i++) {
      var entry = MAP[i];
      if (taken[entry.key]) continue;
      if (entry.list) {
        var items = listOf(entry.name);
        if (items === null) continue;
        for (var j = 0; j < items.length; j++) {
          if (uncovered(items[j]) === null) continue;
          out.push({ el: items[j], code: entry.key + String(j + 1), kind: "key", focus: true, fixed: true });
        }
        continue;
      }
      var el = entry.name === "call" ? callMark() : topmost(entry.name);
      if (el === null || uncovered(el) === null) continue;
      out.push({ el: el, code: entry.key, kind: kindOf(el), focus: true, fixed: true });
    }
    return out;
  }

  // ── Zones, and the one the focus is in (D738) ──

  function zones(found) {
    var list = [];
    var byEl = new Map();
    for (var i = 0; i < found.length; i++) {
      var z = found[i].el.closest(ZONE);
      var zone = byEl.get(z);
      if (zone === undefined) {
        zone = { el: z, name: z === null ? "" : z.getAttribute("data-keytip-zone") || "", items: [], prefix: "" };
        byEl.set(z, zone);
        list.push(zone);
      }
      zone.items.push(found[i]);
    }
    return { list: list, byEl: byEl };
  }

  // The focused chat row or person, if nothing covers it (D740).
  function focusedObject() {
    var active = document.activeElement;
    if (!(active instanceof Element)) return null;
    var object = active.closest(OBJECT);
    return object !== null && uncovered(object) !== null ? object : null;
  }

  function focusZone(all, object) {
    var active = document.activeElement;
    // Focus outside every zone — `<body>`, or a root that takes it — is no
    // zone's: it counts as none.
    var z = active instanceof Element ? active.closest(ZONE) : null;
    if (z !== null) {
      var zone = all.byEl.get(z);
      if (zone !== undefined) return zone;
      // The object's zone may hold nothing else that takes a key and is
      // still where the focus is.
      if (object !== null) {
        zone = { el: z, name: z.getAttribute("data-keytip-zone") || "", items: [], prefix: "" };
        all.byEl.set(z, zone);
        all.list.push(zone);
        return zone;
      }
    }
    var fallbacks = ["composer", "list"];
    for (var f = 0; f < fallbacks.length; f++) {
      for (var i = 0; i < all.list.length; i++) {
        if (all.list[i].name === fallbacks[f]) return all.list[i];
      }
    }
    return all.list.length ? all.list[0] : null;
  }

  // ── The dynamic keys (D739, D749) ──

  function declared(el) {
    return (el.getAttribute("data-keytip") || "").trim().toUpperCase();
  }

  // The object's actions, read off the hidden copy of its menu (D740): its
  // rows, less those that open a submenu and the disabled lines of its foot.
  function actionsOf(object) {
    if (object === null) return [];
    var holder = document.querySelector(ACTIONS);
    if (holder === null) return [];
    // The desktop's rows are `ds::MenuItem`s; the web writes its rows by hand,
    // with the class and not always the role.
    var rows = holder.querySelectorAll("[role=menuitem], .echo-menu__item");
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (disabled(row) || row.hasAttribute("aria-haspopup")) continue;
      var label = row.querySelector(".echo-menu__label");
      var words = ((label !== null ? label.textContent : row.textContent) || "").trim();
      if (words) out.push({ el: row, words: words, kind: "action", own: "" });
    }
    return out;
  }

  // Every item of a zone its own key: the letter it declares when that is
  // free, then the rows the digits and the rest the letters, in their order on
  // the page; what one key cannot cover gets two, under prefixes taken from
  // the end of the order, so no key is the start of another.
  function own(items, letters) {
    var used = {};
    var rest = [];
    for (var i = 0; i < items.length; i++) {
      var d = declared(items[i].el);
      if (d.length === 1 && !used[d] && (letters.indexOf(d) >= 0 || DIGITS.indexOf(d) >= 0)) {
        items[i].own = d;
        used[d] = true;
      } else {
        rest.push(items[i]);
      }
    }
    var freeLetters = [];
    var freeDigits = [];
    for (var l = 0; l < letters.length; l++) if (!used[letters.charAt(l)]) freeLetters.push(letters.charAt(l));
    for (var g = 0; g < DIGITS.length; g++) if (!used[DIGITS.charAt(g)]) freeDigits.push(DIGITS.charAt(g));
    var all = letters + DIGITS;
    var free = freeLetters.length + freeDigits.length;
    var prefixes = [];
    if (rest.length > free && free > 0) {
      var k = 1;
      while (free - k + k * all.length < rest.length && k < free) k++;
      for (var p = 0; p < k; p++) {
        prefixes.unshift(freeLetters.length ? freeLetters.pop() : freeDigits.pop());
      }
    }
    var pairs = [];
    for (var a = 0; a < prefixes.length; a++) {
      for (var b = 0; b < all.length; b++) pairs.push(prefixes[a] + all.charAt(b));
    }
    for (var r = 0; r < rest.length; r++) {
      var item = rest[r];
      var first = item.row ? freeDigits : freeLetters;
      var second = item.row ? freeLetters : freeDigits;
      if (first.length) item.own = first.shift();
      else if (second.length) item.own = second.shift();
      else if (pairs.length) item.own = pairs.shift();
    }
  }

  function plan() {
    var statics = fixed();
    var skip = new Set();
    for (var x = 0; x < statics.length; x++) skip.add(statics[x].el);
    var found = collect(skip);
    var all = zones(found);
    var object = focusedObject();
    var actions = actionsOf(object);
    var focus = focusZone(all, actions.length ? object : null);
    var taken = takenLetters();
    var letters = "";
    for (var i = 0; i < ORDER.length; i++) if (!taken[ORDER.charAt(i)]) letters += ORDER.charAt(i);

    // The object's actions first: their letters are the focus zone's, and
    // its own elements give them up (D740).
    var reserved = {};
    for (var a = 0; a < actions.length; a++) {
      var d = declared(actions[a].el);
      if (d.length === 1 && letters.indexOf(d) >= 0 && !reserved[d]) {
        actions[a].own = d;
        reserved[d] = true;
      }
    }
    for (var b = 0; b < actions.length; b++) {
      if (actions[b].own) continue;
      for (var c = 0; c < letters.length; c++) {
        if (!reserved[letters.charAt(c)]) {
          actions[b].own = letters.charAt(c);
          reserved[letters.charAt(c)] = true;
          break;
        }
      }
    }

    // Every zone's own keys — the focus zone's less what the object holds.
    for (var z = 0; z < all.list.length; z++) {
      var zone = all.list[z];
      var allowed = letters;
      if (zone === focus) {
        allowed = "";
        for (var e = 0; e < letters.length; e++) if (!reserved[letters.charAt(e)]) allowed += letters.charAt(e);
      }
      own(zone.items, allowed);
    }

    // Each other zone's letter: its own while the focus zone leaves it free,
    // else the first that is.
    var firsts = {};
    var codes = [];
    if (focus !== null) {
      for (var f = 0; f < focus.items.length; f++) if (focus.items[f].own) firsts[focus.items[f].own.charAt(0)] = true;
    }
    for (var h = 0; h < actions.length; h++) if (actions[h].own) firsts[actions[h].own] = true;
    var others = all.list.filter(function (zone) {
      return zone !== focus && zone.items.length > 0;
    });
    others.sort(function (x, y) {
      var ix = ZONE_ORDER.indexOf(x.name);
      var iy = ZONE_ORDER.indexOf(y.name);
      return (ix < 0 ? ZONE_ORDER.length : ix) - (iy < 0 ? ZONE_ORDER.length : iy);
    });
    var pool = "";
    for (var fb = 0; fb < FALLBACK.length; fb++) if (letters.indexOf(FALLBACK.charAt(fb)) >= 0) pool += FALLBACK.charAt(fb);
    pool += DIGITS;
    for (var o = 0; o < others.length; o++) {
      var want = ZONE_LETTER[others[o].name] || WINDOW_LETTER;
      if (taken[want] || firsts[want] || letters.indexOf(want) < 0) {
        want = "";
        for (var q = 0; q < pool.length; q++) {
          if (!firsts[pool.charAt(q)]) {
            want = pool.charAt(q);
            break;
          }
        }
      }
      others[o].prefix = want;
      if (want) firsts[want] = true;
    }

    for (var y = 0; y < all.list.length; y++) {
      var zn = all.list[y];
      var near = zn === focus;
      for (var n = 0; n < zn.items.length; n++) {
        var it = zn.items[n];
        if (!it.own || (!near && !zn.prefix)) continue;
        codes.push({ el: it.el, code: near ? it.own : zn.prefix + it.own, kind: it.kind, focus: near });
      }
    }
    var acts = [];
    for (var s = 0; s < actions.length; s++) {
      if (!actions[s].own) continue;
      acts.push({
        el: actions[s].el,
        code: actions[s].own,
        kind: "action",
        focus: true,
        words: actions[s].words,
        anchor: object,
      });
    }
    return { fixed: statics, entries: codes, actions: acts, object: actions.length ? object : null };
  }

  // ── Drawing (D741) ──

  function box() {
    var mount = document.querySelector(MOUNT);
    if (mount === null) return null;
    var b = mount.querySelector(":scope > ." + BOX);
    if (b === null) {
      b = document.createElement("div");
      b.className = BOX;
      b.setAttribute("aria-hidden", "true");
      mount.appendChild(b);
    }
    return b;
  }

  function clear() {
    var mount = document.querySelector(MOUNT);
    var b = mount === null ? null : mount.querySelector(":scope > ." + BOX);
    if (b !== null) b.textContent = "";
  }

  function caps(into, code, sofar) {
    var keys = document.createElement("span");
    keys.className = "echo-tip__keys";
    for (var i = 0; i < code.length; i++) {
      var cap = document.createElement("span");
      cap.className = i < sofar.length ? "echo-keycap echo-keycap--typed" : "echo-keycap";
      cap.textContent = code.charAt(i);
      keys.appendChild(cap);
    }
    into.appendChild(keys);
  }

  function still(code, sofar) {
    return sofar === "" || (code.length > sofar.length && code.indexOf(sofar) === 0);
  }

  function rect(p, size) {
    return { x: p.x, y: p.y, w: size.w, h: size.h };
  }

  function placeAt(r, size, side, align, avoid) {
    var anchor = { x: r.left, y: r.top, w: r.width, h: r.height };
    var bounds = { w: window.innerWidth, h: window.innerHeight };
    if (typeof window.__echoPlace === "function") {
      return window.__echoPlace(anchor, size, bounds, { side: side, align: align, offset: GAP, edge: EDGE, avoid: avoid });
    }
    var x = Math.max(EDGE, Math.min(r.left + r.width / 2 - size.w / 2, bounds.w - EDGE - size.w));
    return { x: Math.round(x), y: Math.round(r.top - GAP - size.h), fits: r.top - GAP - size.h >= EDGE };
  }

  // A key alone: inside a wide thing — a row, a field — at the left of its
  // middle; on a compact one, over its top-left corner.
  function pin(node, r) {
    var w = node.offsetWidth;
    var h = node.offsetHeight;
    var wide = r.width >= 3 * r.height;
    var x = wide ? r.left + 4 : r.left - 4;
    var y = wide ? r.top + (r.height - h) / 2 : r.top - 4;
    x = Math.max(2, Math.min(x, window.innerWidth - 2 - w));
    y = Math.max(2, Math.min(y, window.innerHeight - 2 - h));
    node.style.left = Math.round(x) + "px";
    node.style.top = Math.round(y) + "px";
  }

  function draw(p, sofar) {
    var b = box();
    if (b === null) return;
    b.textContent = "";
    var placed = [];
    // The object's column, first: the rest keep clear of it.
    if (p.object !== null) {
      var column = document.createElement("div");
      column.className = "echo-keytips-actions";
      for (var a = 0; a < p.actions.length; a++) {
        if (!still(p.actions[a].code, sofar)) continue;
        var line = document.createElement("div");
        line.className = "echo-keytips-actions__line";
        var said = document.createElement("span");
        said.className = "echo-keytips-actions__words";
        said.textContent = p.actions[a].words;
        line.appendChild(said);
        caps(line, p.actions[a].code, sofar);
        column.appendChild(line);
      }
      if (column.childNodes.length) {
        b.appendChild(column);
        var size = { w: column.offsetWidth, h: column.offsetHeight };
        var at = placeAt(p.object.getBoundingClientRect(), size, "right", "start", placed);
        column.style.left = at.x + "px";
        column.style.top = at.y + "px";
        placed.push(rect(at, size));
      }
    }
    var alone = [];
    // The map's first, then the focus zone's: they take the free places
    // before the rest.
    var order = p.fixed.concat(
      p.entries.filter(function (x) {
        return x.focus;
      }),
      p.entries.filter(function (x) {
        return !x.focus;
      }),
    );
    for (var i = 0; i < order.length; i++) {
      var e = order[i];
      if (!still(e.code, sofar)) continue;
      if (e.kind !== "icon") {
        alone.push(e);
        continue;
      }
      var node = document.createElement("div");
      node.className = "echo-tip";
      var label = document.createElement("span");
      label.className = "echo-tip__label";
      label.textContent = window.__echoTips ? window.__echoTips.words(e.el) : "";
      node.appendChild(label);
      caps(node, e.code, sofar);
      b.appendChild(node);
      var dims = { w: node.offsetWidth, h: node.offsetHeight };
      var spot = placeAt(e.el.getBoundingClientRect(), dims, e.el.getAttribute("data-tip-side") || "top", "center", placed);
      if (spot.fits) {
        node.style.left = spot.x + "px";
        node.style.top = spot.y + "px";
        placed.push(rect(spot, dims));
      } else {
        // No free place: the key alone, on the control's corner.
        b.removeChild(node);
        alone.push(e);
      }
    }
    for (var j = 0; j < alone.length; j++) {
      var key = document.createElement("div");
      key.className = "echo-keytip";
      caps(key, alone[j].code, sofar);
      b.appendChild(key);
      pin(key, alone[j].el.getBoundingClientRect());
    }
  }

  // ── Pressing (D736) ──

  function press(entry) {
    var el = entry.el;
    if (!el.isConnected) return;
    if (entry.kind === "field") {
      el.focus();
      // A one-line field with words in it: what is typed next replaces them,
      // as a search box opened from the keyboard does. Never the composer's
      // box, whose words are a draft: its caret goes where it was, or to the
      // end of the draft if it was never anywhere.
      if (el.tagName === "INPUT" && el.type !== "range" && el.value && typeof el.select === "function") {
        el.select();
      } else if (el.tagName === "TEXTAREA" && el.value && el.selectionStart === 0 && el.selectionEnd === 0) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
      return;
    }
    // The focus stays where it is — the composer keeps its caret, as a click
    // on its buttons does — and the control is named for the one question a
    // press with no pointer leaves open: where to stand a menu it opens. The
    // desktop asks the page for the focused control's middle there
    // (dioxus-plan.md D307, `ds::overlays::FOCUSED`), and looks here first. An
    // action's row is hidden: its menu stands by the object it is about.
    anchor(entry.anchor || el);
    el.click();
  }

  var anchorTimer = 0;

  function anchor(el) {
    window.__echoKeytipAnchor = el;
    if (anchorTimer) window.clearTimeout(anchorTimer);
    // Long enough for the page to be asked once (one round trip); gone before
    // a later press with a pointer could be anchored to it.
    anchorTimer = window.setTimeout(function () {
      anchorTimer = 0;
      window.__echoKeytipAnchor = null;
    }, 1500);
  }

  // A code of the dynamic keys; the map's are pressed by their chords.
  function find(p, code) {
    var lists = [p.actions, p.entries];
    for (var l = 0; l < lists.length; l++) {
      for (var i = 0; i < lists[l].length; i++) {
        if (lists[l][i].code === code) return lists[l][i];
      }
    }
    return null;
  }

  function starts(p, sofar) {
    var lists = [p.actions, p.entries];
    for (var l = 0; l < lists.length; l++) {
      for (var i = 0; i < lists[l].length; i++) {
        if (still(lists[l][i].code, sofar)) return true;
      }
    }
    return false;
  }

  // ── The hold (D736) ──

  function off() {
    var root = document.querySelector("[data-keytips]");
    return root !== null && root.getAttribute("data-keytips") === "off";
  }

  function send(msg) {
    if (typeof window.__echoKeytipsRelay === "function") window.__echoKeytipsRelay(msg);
  }

  function stopTimer() {
    if (timer) {
      window.clearTimeout(timer);
      timer = 0;
    }
  }

  // Back to nothing: Ctrl is up.
  function end() {
    stopTimer();
    if (relaying) send("down");
    relaying = false;
    drawn = null;
    typed = "";
    clear();
    state = "idle";
  }

  // Nothing more this hold: down until Ctrl is let go.
  function spend() {
    stopTimer();
    if (relaying) send("down");
    relaying = false;
    drawn = null;
    typed = "";
    clear();
    state = "spent";
  }

  function hold() {
    typed = "";
    drawn = null;
    state = "held";
  }

  function raise() {
    timer = 0;
    if (state !== "held") return;
    state = "up";
    if (relaying) {
      send("up");
      return;
    }
    drawn = plan();
    if (window.__echoTips) window.__echoTips.hide();
    draw(drawn, "");
  }

  // One key of a dynamic code, while the keys are drawn (D749). Whether the
  // mode spent it — then nobody else hears it.
  function key(symbol) {
    if (!symbol || (ORDER.indexOf(symbol) < 0 && DIGITS.indexOf(symbol) < 0)) return false;
    if (state !== "up" && state !== "narrowed") return false;
    var sofar = typed + symbol;
    var hit = find(drawn, sofar);
    if (hit !== null) {
      spend();
      press(hit);
      return true;
    }
    if (!starts(drawn, sofar)) return false;
    typed = sofar;
    state = "narrowed";
    draw(drawn, typed);
    return true;
  }

  function symbolOf(e) {
    var code = e.code || "";
    if (code.length === 4 && code.lastIndexOf("Key", 0) === 0) return code.charAt(3);
    if (code.length === 6 && code.lastIndexOf("Digit", 0) === 0) return code.charAt(5);
    if (code.length === 7 && code.lastIndexOf("Numpad", 0) === 0 && /[0-9]/.test(code.charAt(6))) return code.charAt(6);
    return "";
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  document.addEventListener(
    "keydown",
    function (e) {
      // A chord being assigned (the desktop's settings, `#hotkey-capture`) is
      // the page's to read, letter and all: the map pressing Ctrl+H there
      // would end the call, and no chord of the map could ever be assigned.
      // The window's own chord listener stands down the same way (platform.rs).
      if (document.getElementById("hotkey-capture") !== null) {
        drop();
        if (state !== "idle") end();
        return;
      }
      if (e.key === "Control") {
        if (e.repeat || state !== "idle" || walk !== null) return;
        if (e.shiftKey || e.altKey || e.metaKey || e.isComposing || off()) return;
        hold();
        relaying = document.querySelector(RELAY) !== null;
        if (relaying) send("hold");
        timer = window.setTimeout(raise, HOLD);
        return;
      }
      var symbol = symbolOf(e);
      // A list's chord, Ctrl still held (D748): its letter moves the frame,
      // a digit types the number, Escape drops it, and any other key drops it
      // and goes on.
      if (walk !== null) {
        if (e.key === "Shift") return;
        if (e.ctrlKey && !e.altKey && !e.metaKey && !e.isComposing) {
          if (symbol === walk.entry.key) {
            stop(e);
            move(e.shiftKey ? -1 : 1);
            return;
          }
          if (!e.shiftKey && symbol && DIGITS.indexOf(symbol) >= 0) {
            stop(e);
            digit(symbol);
            return;
          }
        }
        if (e.key === "Escape") {
          stop(e);
          drop();
          return;
        }
        drop();
      }
      // Ctrl let go where this window could not hear it: nothing of the mode's.
      if (!e.ctrlKey) {
        if (state !== "idle") end();
        return;
      }
      // A chord with more than Ctrl and Shift is nobody's here.
      if (e.altKey || e.metaKey || e.isComposing) {
        if (state === "held" || state === "up" || state === "narrowed") spend();
        return;
      }
      // The map, whatever the hold is doing (D747).
      if (hotkey(e, symbol)) {
        stop(e);
        return;
      }
      if (state === "idle" || state === "spent") return;
      if (e.shiftKey) {
        spend();
        return;
      }
      if (e.key === "Escape") {
        if (state === "up" || state === "narrowed") stop(e);
        spend();
        return;
      }
      // Blind, only the map (D749): before the keys are up, a key cancels the
      // hold and goes on.
      if (state === "held") {
        spend();
        return;
      }
      if (relaying) {
        // The keys are the card's, in the other window, which knows its own
        // codes and keeps its own one press (D744). A key that cannot be one —
        // a chord's letter of the reader's own among them — ends the hold here
        // and goes on.
        if (symbol && !takenLetters()[symbol] && (ORDER.indexOf(symbol) >= 0 || DIGITS.indexOf(symbol) >= 0)) {
          stop(e);
          send("key:" + symbol);
        } else {
          spend();
        }
        return;
      }
      if (key(symbol)) stop(e);
      else spend();
    },
    true,
  );

  document.addEventListener(
    "keyup",
    function (e) {
      if (e.key !== "Control") return;
      if (walk !== null) commit();
      if (state !== "idle") end();
    },
    true,
  );

  function cancel() {
    drop();
    if (state === "held" || state === "up" || state === "narrowed") spend();
  }

  document.addEventListener(
    "pointerdown",
    function () {
      // A press with a pointer is placed by the pointer.
      window.__echoKeytipAnchor = null;
      cancel();
    },
    true,
  );
  document.addEventListener("wheel", cancel, { capture: true, passive: true });
  window.addEventListener("blur", function () {
    drop();
    if (state !== "idle") end();
  });
  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) return;
    drop();
    if (state !== "idle") end();
  });

  // The relay's far end (D744): this window draws and presses for one that has
  // the keys. The keys it is sent are the ones that window would not take.
  function remote(msg) {
    msg = String(msg);
    if (msg === "hold") {
      relaying = false;
      stopTimer();
      clear();
      hold();
    } else if (msg === "up") {
      if (state === "held") raise();
    } else if (msg === "down") {
      end();
    } else if (msg.lastIndexOf("key:", 0) === 0) {
      if (state === "held" || state === "up" || state === "narrowed") {
        if (!key(msg.slice(4))) spend();
      }
    }
  }

  window.__echoKeytips = {
    up: function () {
      return state === "up" || state === "narrowed";
    },
    plan: function () {
      var p = plan();
      return p.actions.concat(p.fixed, p.entries);
    },
    map: function () {
      return MAP.map(function (entry) {
        return { name: entry.name, keys: "Ctrl+" + entry.key };
      });
    },
    chord: chord,
    remote: remote,
  };
})();
